import type { LucideIcon } from 'lucide-react';
import { BookOpen, CalendarCheck, Users } from 'lucide-react';
import type { ReportAction, ReportRecommendation, ReportResource } from '../api/reportApi';

/**
 * One Top-Opportunity action-plan card (Feature 095, design handoff #2): rank +
 * pillar name + Core Question + pillar score; "What this means"; three numbered
 * actions each with an Explore & Learn / Plan / Connect grid (links where a
 * Resource Hub slug is present, plain labels otherwise; empty groups hidden);
 * "Need additional guidance?" + service; and an advisor-conversation note where
 * the framework defines one.
 */

function ResourceGroup({
  icon: Icon,
  label,
  items,
}: {
  icon: LucideIcon;
  label: string;
  items: ReportResource[];
}) {
  if (!items.length) return null;
  return (
    <div className="min-w-0 sm:flex-1">
      <div className="mb-2 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 flex-shrink-0 text-[#026857]" />
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#026857]">
          {label}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {items.map((res, i) => {
          // Stable identity + index tiebreaker (the type doesn't guarantee unique
          // slugs/labels; the list is static). Link to the Resource Hub (seoUrl) or
          // straight to a bQuest platform feature (href); plain text otherwise.
          const key = `${res.seoUrl ?? res.href ?? res.label}-${i}`;
          const to = res.seoUrl
            ? `/resource-hub-detail/${encodeURIComponent(res.seoUrl)}`
            : res.href;
          return to ? (
            <a
              key={key}
              href={to}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] leading-snug text-[#026857] underline decoration-[#026857]/30 underline-offset-2 hover:decoration-[#026857]"
            >
              {res.label}
            </a>
          ) : (
            <span key={key} className="text-[13px] leading-snug text-gray-600">
              {res.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Action({ action }: { action: ReportAction }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-[#fbf9f5] p-4 sm:p-5">
      <div className="mb-1.5 flex gap-2">
        <span className="flex-shrink-0 font-serif text-[15px] text-[#026857]">{action.num}.</span>
        <span className="text-[15px] font-semibold text-gray-900">{action.title}</span>
      </div>
      <p className="ml-6 mb-4 text-[13.5px] leading-relaxed text-gray-600">{action.body}</p>
      <div className="ml-6 flex flex-col gap-4 sm:flex-row sm:gap-6">
        <ResourceGroup icon={BookOpen} label="Explore & Learn" items={action.learn} />
        <ResourceGroup icon={CalendarCheck} label="Plan" items={action.plan} />
        <ResourceGroup icon={Users} label="Connect" items={action.connect} />
      </div>
    </div>
  );
}

export function ActionPlanCard({ rec }: { rec: ReportRecommendation }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
      {/* Header: rank + name + core question + pillar score */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-4">
        <div className="min-w-[16rem] flex-1">
          <div className="mb-1.5 flex items-center gap-2.5">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#f3c66c] text-[12px] font-semibold text-[#3a2a08]">
              {rec.rank}
            </span>
            <h3 className="font-serif text-xl text-gray-900">{rec.name}</h3>
          </div>
          <p className="font-serif text-lg leading-relaxed text-gray-700">{rec.core}</p>
        </div>
        <div className="text-right">
          <div className="font-serif text-2xl text-[#f3c66c]">{rec.pctLabel}</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Pillar score</div>
        </div>
      </div>

      {/* What this means */}
      <div className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-[#026857]">
        What this means
      </div>
      <p className="mt-2 text-[14.5px] leading-relaxed text-gray-600">{rec.means}</p>

      {/* Personalized action plan */}
      <div className="mb-3.5 mt-6 text-[11px] font-semibold uppercase tracking-wider text-[#026857]">
        Your personalized action plan
      </div>
      <div className="flex flex-col gap-4">
        {rec.actions.map((action) => (
          <Action key={action.num} action={action} />
        ))}
      </div>

      {/* Need additional guidance? */}
      <div className="mt-5 flex items-start gap-3.5 rounded-xl border border-[#026857]/15 bg-[#eaf1ee] p-5">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#026857] text-white">
          <Users className="h-4 w-4" />
        </span>
        <div>
          <div className="text-[13px] font-semibold text-gray-900">Need additional guidance?</div>
          <p className="mt-1 text-[13.5px] leading-relaxed text-gray-600">{rec.guidance}</p>
          <div className="mt-2.5 font-serif text-[13.5px] text-[#026857]">{rec.serviceName}</div>
          <div className="mt-0.5 text-[13px] leading-relaxed text-gray-600">{rec.serviceDesc}</div>
          {rec.serviceHref && (
            <a
              href={rec.serviceHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2.5 inline-block text-[13px] font-medium text-[#026857] underline decoration-[#026857]/30 underline-offset-2 hover:decoration-[#026857]"
            >
              Meet your Care Concierge →
            </a>
          )}
        </div>
      </div>

      {/* Advisor conversation */}
      {rec.hasAdvisorNote && rec.advisorNote && (
        <div className="mt-4 border-l-[3px] border-[#026857]/30 pl-3.5">
          <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-gray-400">
            Advisor conversation
          </div>
          <p className="text-[13.5px] leading-relaxed text-gray-600">{rec.advisorNote}</p>
        </div>
      )}
    </div>
  );
}
